import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import {
    VerifiedUser as VerifiedIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    HourglassEmpty as HourglassIcon,
    Block as BlockIcon,
} from '@mui/icons-material';
import { KYCStatusValue } from '../../api/kyc';

interface KYCStatusBadgeProps {
    status: KYCStatusValue | null;
    size?: 'small' | 'medium';
    showIcon?: boolean;
}

interface BadgeConfig {
    label: string;
    color: 'success' | 'warning' | 'error' | 'info' | 'default';
    icon?: React.ReactElement;
}

const getBadgeConfig = (status: KYCStatusValue | null): BadgeConfig => {
    switch (status) {
        case 'APPROVED':
            return {
                label: 'Verified',
                color: 'success',
                icon: <VerifiedIcon />,
            };

        case 'PENDING':
            return {
                label: 'Not Verified',
                color: 'warning',
                icon: <WarningIcon />,
            };

        case 'IN_REVIEW':
            return {
                label: 'In Review',
                color: 'info',
                icon: <HourglassIcon />,
            };

        case 'ACTION_REQUIRED':
            return {
                label: 'Action Required',
                color: 'warning',
                icon: <WarningIcon />,
            };

        case 'ON_HOLD':
            return {
                label: 'On Hold',
                color: 'info',
                icon: <HourglassIcon />,
            };

        case 'REJECTED':
        case 'REJECTED_FINAL':
            return {
                label: 'Rejected',
                color: 'error',
                icon: <ErrorIcon />,
            };

        case 'SUSPENDED':
            return {
                label: 'Suspended',
                color: 'error',
                icon: <BlockIcon />,
            };

        case 'EXPIRED':
            return {
                label: 'Expired',
                color: 'warning',
                icon: <WarningIcon />,
            };

        default:
            return {
                label: 'Unknown',
                color: 'default',
            };
    }
};

const KYCStatusBadge: React.FC<KYCStatusBadgeProps> = ({
    status,
    size = 'small',
    showIcon = false,
}) => {
    const config = getBadgeConfig(status);

    const chipProps: ChipProps = {
        label: config.label,
        color: config.color,
        size,
        variant: 'filled',
    };

    if (showIcon && config.icon) {
        chipProps.icon = config.icon;
    }

    return <Chip {...chipProps} />;
};

export default KYCStatusBadge;
